import React, {useState, useEffect, useMemo, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import AddClientModal from '../components/AddClientModal';
import ErrorToast from '../components/ErrorToast';
import { useAuth } from '../context/AuthContext';
import {getClientList, getTotalActiveClients} from '../services/client-service';
import Client from '../models/Client';
import { ClientType } from '../models/enums/ClientType';
import { ClientStatus } from '../models/enums/ClientStatus';
import ClientSearchParams, {SortField} from "../models/ClientSearchParams";
import EditClientModal from "../components/EditClientModal";
import DeleteClientModal from "../components/DeleteClientModal";
import {useDebounce} from "../utils/CommonUtils";
import {SubscriptionType} from "../models/enums/SubscriptionType";
import UpgradeButton from "../components/UpgradeButton";

const ClientsPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, subscriptionPlan } = useAuth();

    const [clients, setClients] = useState<Client[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorToast, setErrorToast] = useState('');
    const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
    const [isDeleteClientModalOpen, setIsDeleteClientModalOpen] = useState(false);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);

    const [selectedTypes, setSelectedTypes] = useState<ClientType[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<ClientStatus[]>(location.state?.filters?.status ? [location.state.filters.status] : []);
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
    const [totalClients, setTotalClients] = useState<number>(0);
    const [disableAddClient, setDisableAddClient] = useState(true);

    const [showFilters, setShowFilters] = useState(!!location.state?.filters);

    const isFirstRender = useRef(true);

    useEffect(() => {
        if (subscriptionPlan?.maxClients && totalClients >= subscriptionPlan.maxClients) {
            setDisableAddClient(true);
        } else {
            setDisableAddClient(false);
        }
    }, [totalClients, subscriptionPlan]);

    const toggleType = (type: ClientType) => {
        setPage(1);
        setSelectedTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };
    const toggleStatus = (status: ClientStatus) => {
        setPage(1);
        setSelectedStatuses(prev =>
            prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
        );
    };

    const fetchClients = async () => {
        if (!user?.id) return;
        if (debouncedSearch && debouncedSearch.length < 3) {
            return;
        }

        setLoading(true);
        try {
            const params: ClientSearchParams = {
                page: page,
                pageSize: pageSize,
                search: debouncedSearch,
                types: selectedTypes.length > 0 ? selectedTypes : null,
                statuses: selectedStatuses.length > 0 ? selectedStatuses : null,
                dateFrom: dateFrom,
                dateTo: dateTo,
                sortBy: sortField,
                sortDir: sortDir,
                userId: user.id
            };
            const response = await getClientList(params);
            if (response.status === 200 && response.data) {
                setClients(response.data.clients);
                setTotalCount(response.data.totalCount);
            } else {
                console.error('Error fetching clients');
                setErrorToast('Error retrieving client list');
            }
        } catch (err) {
            console.error('Error fetching clients', err);
            setErrorToast('Error retrieving client list');
        } finally {
            setLoading(false);
        }
    };

    const editClient = (e: React.MouseEvent<HTMLButtonElement>, client: Client) => {
        e.stopPropagation();
        setSelectedClient(client);
        setIsEditClientModalOpen(true);
    }

    const deleteClient = (e: React.MouseEvent<HTMLButtonElement>, client: Client) => {
        e.stopPropagation();
        setSelectedClient(client);
        setIsDeleteClientModalOpen(true);
    }

    useEffect(() => {
        const getTotalClients = async () => {
            if (isFirstRender.current && user && user.id) {
                try {
                    const response = await getTotalActiveClients(user?.id);
                    if (response.status === 200 && response.data) {
                        setTotalClients(response.data);
                        isFirstRender.current = false;
                    }
                } catch (err: any) {
                    console.error(err);
                }
            }
        }
        getTotalClients();
    }, [user]);

    useEffect(() => {
        fetchClients();
    }, [user, page, pageSize, debouncedSearch, selectedTypes, selectedStatuses, dateFrom, dateTo, sortField, sortDir]);

    const onClientAdded = () => {
        setTotalClients(prev => prev + 1);
        fetchClients();
    };

    const handleClientUpdate =  (updatedClient: Client) => {
        setClients(prev =>
            prev.map(client => (client.id === updatedClient.id ? updatedClient : client))
        );
    }

    const handleDeleteClient = (clientId: number) => {
        setTotalClients(prev => prev - 1);
        setClients(prev =>
            prev.filter(client => client.id !== clientId)
        );
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-grow container mx-auto px-4 py-6">
                {/* Top controls */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                    <div className="flex items-center space-x-2">
                        {disableAddClient && subscriptionPlan?.subscriptionType === SubscriptionType.FREE && (
                            <UpgradeButton />
                        )}
                        <button
                            className={`px-4 py-2 rounded ${
                                disableAddClient
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300'
                                    : 'bg-crumborange text-white hover:bg-orange-600'
                            }`}
                            onClick={() => setIsAddClientModalOpen(true)}
                            disabled={disableAddClient}
                            data-testid='add-client-btn'
                            title={disableAddClient ? 'Reached max clients for your plan' : undefined}
                        >
                            Add Client
                        </button>
                        <input
                            type="text"
                            placeholder="Search clients..."
                            className="border rounded p-2"
                            value={searchTerm}
                            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                        />
                    </div>
                    <div className="flex items-center space-x-2">
                        <select
                            data-testid="page-size"
                            className="border rounded p-2"
                            value={pageSize}
                            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                        >
                            {[10, 20, 50, 100].map(size => (
                                <option key={size} value={size}>{size} per page</option>
                            ))}
                        </select>
                        <select
                            data-testid="sort-by"
                            className="border rounded p-2"
                            value={sortField}
                            onChange={e => setSortField(e.target.value as SortField)}
                        >
                            <option value="name">Name</option>
                            <option value="type">Type</option>
                            <option value="status">Status</option>
                            <option value="lastActivityDate">Last Activity</option>
                        </select>
                        <select
                            data-testid="sort-dir"
                            value={sortDir}
                            onChange={e => setSortDir(e.target.value as any)}
                            className="border rounded p-1"
                        >
                            <option value="ASC">Ascending</option>
                            <option value="DESC">Descending</option>
                        </select>
                        <button
                            data-testid="filters"
                            className={`border rounded p-2 ${showFilters ? 'bg-crumborange text-white' : 'bg-white'}`}
                            onClick={() => setShowFilters(v => !v)}
                        >
                            Filters
                        </button>
                    </div>
                </div>
                {disableAddClient && subscriptionPlan?.maxClients != null && (
                    <div className="mb-3 text-sm text-gray-600" data-testid="client-limit-message">
                        You’ve reached the maximum of <span className="font-semibold">{subscriptionPlan.maxClients}</span> clients for your plan.
                        <span className="ml-1">Upgrade your plan to add more.</span>
                    </div>
                )}
                {/* Filters panel */}
                {showFilters && (
                    <div className="border rounded p-4 mb-4" data-testid="filters-panel">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="pb-5">
                                    <label className="block mb-1">Type</label>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.values(ClientType).map(type => (
                                            <button
                                                key={type}
                                                type="button"
                                                className={`px-3 py-1 border rounded cursor-pointer ${
                                                    selectedTypes.includes(type)
                                                        ? 'bg-crumborange text-white'
                                                        : 'bg-white'
                                                }`}
                                                onClick={() => toggleType(type)}
                                            >
                                                {type}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block mb-1">Status</label>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.values(ClientStatus).map(status => (
                                            <button
                                                key={status}
                                                type="button"
                                                className={`px-3 py-1 border rounded cursor-pointer ${
                                                    selectedStatuses.includes(status)
                                                        ? 'bg-crumborange text-white'
                                                        : 'bg-white'
                                                }`}
                                                onClick={() => toggleStatus(status)}
                                            >
                                                {status}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label htmlFor="dateFrom" className="block mb-1">Last Activity From</label>
                                <input
                                    id="dateFrom"
                                    type="date"
                                    className="border rounded w-full p-2"
                                    max={dateTo}
                                    value={dateFrom}
                                    onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                                />
                                <label htmlFor="dateTo" className="block mt-2 mb-1">To</label>
                                <input
                                    id="dateTo"
                                    type="date"
                                    className="border rounded w-full p-2"
                                    min={dateFrom}
                                    value={dateTo}
                                    onChange={e => { setDateTo(e.target.value); setPage(1); }}
                                />
                                <div className="flex justify-end space-x-2 mt-4">
                                    <button
                                        className="px-4 py-2 rounded border"
                                        onClick={() => {
                                            setSelectedTypes([]);
                                            setSelectedStatuses([]);
                                            setDateFrom('');
                                            setDateTo('');
                                            setPage(1);
                                        }}
                                    >
                                        Reset Filters
                                    </button>
                                </div>
                            </div>

                        </div>

                    </div>
                )}

                {/* Client table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border rounded">
                        <thead>
                        <tr className="bg-gray-100 text-left">
                            <th className="px-4 py-2" data-testid="Name-header">Name</th>
                            <th className="px-4 py-2" data-testid="Type-header">Type</th>
                            <th className="px-4 py-2" data-testid="Status-header">Status</th>
                            <th className="px-4 py-2" data-testid="Tags-header">Tags</th>
                            <th className="px-4 py-2" data-testid="Last Activity-header">Last Activity</th>
                            <th className="px-4 py-2 " data-testid="Actions-header">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="p-4 text-center">Loading...</td></tr>
                        ) : clients.length > 0 ? (
                            clients.map(client => (
                                <tr
                                    key={client.id}
                                    data-testid={"client-" + client.id}
                                    className="border-t hover:bg-gray-50 cursor-pointer"
                                    onClick={() => navigate(`/client/${client.id}`)}
                                >
                                    <td className="px-4 py-2">{client.name}</td>
                                    <td className="px-4 py-2">{client.type}</td>
                                    <td className="px-4 py-2">{client.status}</td>
                                    <td className="px-4 py-2">{Array.isArray(client.tags) && client.tags.length > 0
                                        ? client.tags.join(', ') : ''}</td>
                                    <td className="px-4 py-2">{client.lastActivityDate.toLocaleDateString()}</td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                        <div className="flex space-x-2">
                                            <button
                                                className="text-crumbblue hover:underline"
                                                data-testid={"edit-client-" + client.id}
                                                onClick={(e) => editClient(e, client)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="text-crumbblue hover:underline"
                                                data-testid={"delete-client-" + client.id}
                                                onClick={(e) => deleteClient(e, client)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="p-4 text-center text-gray-500">
                                    No clients found.
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-4">
                    <div>
                        Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                    </div>
                    <div className="space-x-2">
                        <button
                            className="px-3 py-1 border rounded disabled:opacity-50"
                            disabled={page === 1}
                            onClick={() => setPage(p => Math.max(p - 1, 1))}
                        >
                            Prev
                        </button>
                        <button
                            className="px-3 py-1 border rounded disabled:opacity-50"
                            disabled={page * pageSize >= totalCount}
                            onClick={() => setPage(p => p + 1)}
                        >
                            Next
                        </button>
                    </div>
                </div>

            </main>
            <Footer />

            <ErrorToast message={errorToast} clearError={() => setErrorToast('')} />

            <AddClientModal
                isOpen={isAddClientModalOpen}
                onClose={() => setIsAddClientModalOpen(false)}
                onError={msg => setErrorToast(msg)}
                onAddClient={onClientAdded}
            />
            {selectedClient && (
                <EditClientModal
                    isOpen={isEditClientModalOpen}
                    onClose={() => {
                        setIsEditClientModalOpen(false);
                        setSelectedClient(null);
                    }}
                    onError={(error) => setErrorToast(error)}
                    onEditClient={(updatedClient) => handleClientUpdate(updatedClient)}
                    client={selectedClient}
                    />
            )}
            {selectedClient && selectedClient.id && (
                <DeleteClientModal
                    isOpen={isDeleteClientModalOpen}
                    clientId={selectedClient.id}
                    onClose={() => {
                        setIsDeleteClientModalOpen(false);
                        setSelectedClient(null);
                    }
                    }
                    onDelete={(clientId) => handleDeleteClient(clientId)}
                    onError={(error) => setErrorToast(error)}/>
            )}
        </div>
    );
};

export default ClientsPage;
